import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiChevronLeft, FiChevronRight, FiPackage, FiShoppingCart } from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./Products.css";

import heroBg from "../assets/uploads/gallery/1771694350812-68069707.jpg";

/* ── Helpers ── */
function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}

function fmtPrice(n) {
  return "R " + Number(n).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function shopImgSrc(filename) {
  if (!filename) return null;
  const API_BASE = process.env.REACT_APP_API_URL || "";
  return `${API_BASE}/uploads/shop/${filename}`;
}

function productImgSrc(filename) {
  if (!filename) return null;
  const API_BASE = process.env.REACT_APP_API_URL || "";
  return `${API_BASE}/uploads/products/${filename}`;
}

/* ── Disable right-click ── */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ── Mini shop product slideshow (right panel) ── */
function ShopSlideshow({ products }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  const go = useCallback((dir) => {
    setIdx((prev) => (prev + dir + products.length) % products.length);
  }, [products.length]);

  useEffect(() => {
    if (products.length < 2) return;
    timerRef.current = setInterval(() => go(1), 4000);
    return () => clearInterval(timerRef.current);
  }, [go, products.length]);

  if (!products.length) return (
    <div className="pd-slideshow pd-slideshow--empty">
      <FiPackage size={40} />
      <p>No products available</p>
    </div>
  );

  const p = products[idx];
  const price = Number(p.sale_price || p.price);
  const oldPrice = p.sale_price ? Number(p.price) : null;
  const imgSrc = shopImgSrc(p.image1);

  return (
    <div className="pd-slideshow">
      <div className="pd-slideshow__img-wrap">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={p.name}
            className="pd-slideshow__img"
            draggable="false"
            onDragStart={(e) => e.preventDefault()}
          />
        ) : (
          <div className="pd-slideshow__no-img"><FiPackage size={48} /></div>
        )}
        {p.discount_percent > 0 && (
          <span className="pd-slideshow__badge">-{Math.round(p.discount_percent)}%</span>
        )}
        {products.length > 1 && (
          <>
            <button className="pd-slideshow__nav pd-slideshow__nav--prev" onClick={() => go(-1)} aria-label="Previous">
              <FiChevronLeft size={16} />
            </button>
            <button className="pd-slideshow__nav pd-slideshow__nav--next" onClick={() => go(1)} aria-label="Next">
              <FiChevronRight size={16} />
            </button>
          </>
        )}
      </div>

      <div className="pd-slideshow__body">
        <p className="pd-slideshow__cat">{p.category_name || "Auto Part"}</p>
        <h4 className="pd-slideshow__name">{p.name}</h4>

        <div className="pd-slideshow__prices">
          <span className="pd-slideshow__price">{fmtPrice(price)}</span>
          {oldPrice && <span className="pd-slideshow__old">{fmtPrice(oldPrice)}</span>}
        </div>

        {p.car_make && (
          <p className="pd-slideshow__fitment">
            Fits: {[p.car_make, p.car_model].filter(Boolean).join(" ")}
            {p.car_year_from ? ` (${p.car_year_from}${p.car_year_to ? `–${p.car_year_to}` : "+"})` : ""}
          </p>
        )}

        <Link to={`/shop/${p.slug}`} className="pd-slideshow__btn">
          <FiShoppingCart size={14} /> View Product
        </Link>
      </div>

      {/* Dots */}
      {products.length > 1 && (
        <div className="pd-slideshow__dots">
          {products.map((_, i) => (
            <button
              key={i}
              className={`pd-slideshow__dot${i === idx ? " active" : ""}`}
              onClick={() => setIdx(i)}
              aria-label={`Go to product ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════ */
export default function ProductsPage() {
  useNoContextMenu();

  const [products, setProducts]           = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [activeId, setActiveId]           = useState(null);

  const [shopProducts, setShopProducts]   = useState([]);
  const [shopLoading, setShopLoading]     = useState(true);
  const [randomShop, setRandomShop]       = useState([]);

  const [heroRef, heroInView] = useInView();

  /* ── Fetch products from products table ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/products`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setProducts(data);
          if (data.length) setActiveId(data[0].id);
        }
        setProductsLoading(false);
      })
      .catch(() => setProductsLoading(false));
  }, []);

  /* ── Fetch shop products for slideshow ── */
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/shop/products?limit=50&sort=newest`)
      .then((r) => r.json())
      .then((data) => {
        const prods = data.products || (Array.isArray(data) ? data : []);
        setShopProducts(prods);
        setShopLoading(false);
      })
      .catch(() => setShopLoading(false));
  }, []);

  /* ── Pick 10 random shop products for slideshow ── */
  useEffect(() => {
    if (!shopProducts.length) return;
    const shuffled = [...shopProducts].sort(() => Math.random() - 0.5);
    setRandomShop(shuffled.slice(0, 10));
  }, [shopProducts]);

  const activeProduct = products.find((p) => p.id === activeId) || null;

  /* Products table has a single image_filename column */
  const activeImages = activeProduct
    ? [activeProduct.image_filename].filter(Boolean)
    : [];

  return (
    <main className="pd">

      {/* ══════ HERO ══════ */}
      <section className="pd-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="pd-hero__overlay" />
        <div className="container pd-hero__body" ref={heroRef}>
          <div className={`pd-hero__label fade-in ${heroInView ? "visible" : ""}`}>OUR PRODUCTS</div>
          <h1
            className={`pd-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            QUALITY AUTO<br />PARTS & PRODUCTS
          </h1>
        </div>
        <div className="pd-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>Our Products</span>
        </div>
      </section>

      {/* ══════ MAIN 3-COLUMN LAYOUT ══════ */}
      <section className="pd-layout">
        <div className="container pd-layout__inner">

          {/* ── LEFT: Product list sidebar ── */}
          <aside className="pd-sidebar">
            <div className="pd-sidebar__box">
              <h3 className="pd-sidebar__heading">Our Products</h3>
              {productsLoading ? (
                <div className="pd-sidebar__loading">Loading…</div>
              ) : (
                <ul className="pd-sidebar__list">
                  {products.map((p) => (
                    <li key={p.id}>
                      <button
                        className={`pd-sidebar__item${p.id === activeId ? " pd-sidebar__item--active" : ""}`}
                        onClick={() => setActiveId(p.id)}
                      >
                        <FiArrowRight size={13} className="pd-sidebar__arrow" />
                        {p.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>

          {/* ── CENTER: Product detail ── */}
          <div className="pd-detail">
            {productsLoading ? (
              <div className="pd-detail__loading">Loading product…</div>
            ) : !activeProduct ? (
              <div className="pd-detail__empty">Select a product from the left.</div>
            ) : (
              <>
                <h2 className="pd-detail__title">{activeProduct.title}</h2>
                <div className="pd-detail__desc">
                  {activeProduct.description
                    ? <div dangerouslySetInnerHTML={{ __html: activeProduct.description }} />
                    : <p>No description available.</p>
                  }
                </div>

                {/* Product images grid */}
                {activeImages.length > 0 && (
                  <div className={`pd-detail__imgs pd-detail__imgs--${Math.min(activeImages.length, 2)}`}>
                    {activeImages.slice(0, 4).map((img, i) => (
                      <div key={i} className="pd-detail__img-wrap">
                        <img
                          src={productImgSrc(img)}
                          alt={`${activeProduct.title} ${i + 1}`}
                          loading="lazy"
                          draggable="false"
                          onDragStart={(e) => e.preventDefault()}
                          onContextMenu={(e) => e.preventDefault()}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Read More button */}
                {activeProduct.slug && (
                  <div className="pd-detail__readmore">
                    <Link to={`/products/${activeProduct.slug}`} className="pd-detail__readmore-btn">
                      Read More <FiArrowRight size={14} />
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── RIGHT: Shop product slideshow ── */}
          <aside className="pd-right">
            <div className="pd-right__inner">
              {shopLoading ? (
                <div className="pd-slideshow pd-slideshow--loading">
                  <div className="pd-slideshow__skeleton" />
                </div>
              ) : (
                <ShopSlideshow products={randomShop} />
              )}

              <Link to="/shop" className="pd-right__cta">
                Shop All Products <FiArrowRight size={13} />
              </Link>
            </div>
          </aside>

        </div>
      </section>

    </main>
  );
}